import {headerSizes, tokens} from '@commercial-helios/ui/theme'
import {Provider, Root, Trigger, Portal, Content, Arrow} from '@radix-ui/react-tooltip'
import type {ReactNode} from 'react'
import React from 'react'
import styled, {keyframes} from 'styled-components'

import {text} from '@helios/shared/styles/typography'

interface TooltipProps {
    /**
     * Text displayed in the tooltip
     */
    label: React.ReactNode

    /**
     * Text announced by the screen reader. Provide if it needs to be different than label
     * @default Same as label
     */
    'aria-label'?: string

    children: ReactNode
}

const Tooltip = ({children, label, 'aria-label': ariaLabel}: TooltipProps) => {
    const [isOpen, setOpen] = React.useState(false)

    return (
        <Provider delayDuration={0}>
            <Root open={isOpen}>
                <Trigger
                    asChild
                    onMouseEnter={() => {
                        setOpen(true)
                    }}
                    onMouseLeave={() => {
                        setOpen(false)
                    }}
                    onFocus={() => {
                        setOpen(true)
                    }}
                    onBlur={() => {
                        setOpen(false)
                    }}
                >
                    {children}
                </Trigger>
                <Portal>
                    <StyledContent
                        aria-label={ariaLabel}
                        sideOffset={5}
                        collisionPadding={{top: headerSizes.heightLarge, left: 16}}
                        forceMount
                    >
                        {label}
                        <Arrow />
                    </StyledContent>
                </Portal>
            </Root>
        </Provider>
    )
}

// we use a different animation depending on whether the tooltip appears over
// the trigger (drop down) or above it (raise)
const slideUpAndFade = keyframes`
    0% { 
        opacity: 0;
        transform: translateY(2px); 
    }
    
    100% { 
        opacity: 1;
        transform: translateY(0);
  }
`
const slideDownAndFade = keyframes`
    0% { 
        opacity: 0;
        transform: translateY(-2px); 
    }
    
    100% { 
        opacity: 1;
        transform: translateY(0);
    }
`

const StyledContent = styled(Content)`
    ${text({variant: 'label-base', color: 'inverted'})}

    box-sizing: content-box;
    width: max-content;
    max-width: 300px;
    padding: ${tokens.spacing12};
    background: ${tokens.shade700};
    border: none;
    border-radius: ${tokens.arc4};
    user-select: none;
    animation-duration: ${tokens.smooth};
    animation-timing-function: ease-in-out;
    will-change: transform, opacity;

    &[data-state='delayed-open'] {
        &[data-side='top'] {
            animation-name: ${slideUpAndFade};
        }

        &[data-side='bottom'] {
            animation-name: ${slideDownAndFade};
        }
    }
`

export default Tooltip
